/* ============================================================================
   ZENGEVITY — Zen RC Product Hero
   Gallery thumb swapping, pack (variant) selection, subscription toggle,
   and cross-section pack selection via the `zen-rc:select-pack` event.
   ============================================================================ */

if (!customElements.get('zen-rc-product-hero')) {
  const HERO_SEL = { now: '[data-hp-now]', was: '[data-hp-was]', badge: '[data-hp-save]' };
  const PACK_SEL = { now: '[data-price-now]', was: '[data-price-was]', badge: '[data-pack-badge]' };

  customElements.define(
    'zen-rc-product-hero',
    class ZenRcProductHero extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.variantInput = this.form ? this.form.querySelector('[data-variant-id]') : null;
        this.planInput = this.form ? this.form.querySelector('[data-selling-plan]') : null;
        this.refill = this.querySelector('[data-refill]');
        this.packs = Array.from(this.querySelectorAll('[data-pack]'));
        this.mainImage = this.querySelector('[data-main-image] img');
        this.thumbs = Array.from(this.querySelectorAll('[data-thumb]'));

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', () => this.selectThumb(thumb));
        });

        this.packs.forEach((pack) => {
          const input = pack.querySelector('.zrc-ph__pack-input');
          if (input) input.addEventListener('change', () => this.selectPack(pack));
        });

        if (this.refill) {
          this.refill.addEventListener('change', () => this.syncPrices());
        }

        // Appstle's app embed auto-injects its own widget, with its own
        // selling_plan radios (Subscribe pre-checked), into any product
        // form that has no Appstle app block. The picker owns the plan
        // here, so keep foreign selling_plan fields out of the submission
        // and hide the injected widget, now and whenever it re-renders.
        if (this.form && this.packs.length) {
          this.guardForm();
          this.formObserver = new MutationObserver(() => this.guardForm());
          this.formObserver.observe(this.form, { childList: true, subtree: true });
          this.form.addEventListener('submit', () => this.guardForm(), true);

          // The same embed wraps window.fetch and rewrites every /cart/add
          // body to match its widget's checked radio, blanking the plan when
          // none is checked. It leaves requests carrying its own re-entry
          // marker alone, so the theme's add-to-cart goes out flagged.
          const routes = window.routes;
          if (routes && typeof routes.cart_add_url === 'string' && routes.cart_add_url.indexOf('appstle=true') === -1) {
            routes.cart_add_url += (routes.cart_add_url.indexOf('?') === -1 ? '?' : '&') + 'appstle=true';
          }
        }

        this.onExternalSelect = (event) => {
          const detail = event.detail || {};
          const pack = this.packs[detail.index];
          const input = pack ? pack.querySelector('.zrc-ph__pack-input') : null;

          // Fallback: no matching pack (single-variant product or picker
          // hidden) — still bring the shopper to the buy box.
          if (!input || input.disabled) {
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }

          input.checked = true;
          if (this.refill && typeof detail.subscribe === 'boolean') {
            this.refill.checked = detail.subscribe;
          }
          this.selectPack(pack);
          this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        };
        document.addEventListener('zen-rc:select-pack', this.onExternalSelect);

        this.syncPrices();
      }

      disconnectedCallback() {
        if (this.onExternalSelect) {
          document.removeEventListener('zen-rc:select-pack', this.onExternalSelect);
          this.onExternalSelect = null;
        }
        if (this.formObserver) {
          this.formObserver.disconnect();
          this.formObserver = null;
        }
      }

      guardForm() {
        if (!this.form) return;
        this.form.querySelectorAll('[name="selling_plan"], [name="selling-plan"]').forEach((el) => {
          if (el === this.planInput) return;
          if (!el.disabled) el.disabled = true;
          if (el.checked) el.checked = false;
        });
        this.form.querySelectorAll('.appstle_sub_widget, .appstle_subscription_wrapper').forEach((el) => {
          if (!el.hidden) el.hidden = true;
        });
      }

      selectThumb(thumb) {
        if (!this.mainImage) return;
        this.thumbs.forEach((t) => {
          t.classList.remove('is-active');
          t.setAttribute('aria-pressed', 'false');
        });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-pressed', 'true');

        if (thumb.dataset.fullSrcset) {
          this.mainImage.srcset = thumb.dataset.fullSrcset;
          this.mainImage.sizes = '(min-width: 900px) 540px, 100vw';
        } else {
          this.mainImage.removeAttribute('srcset');
          this.mainImage.removeAttribute('sizes');
        }
        this.mainImage.src = thumb.dataset.fullSrc;
        if (thumb.dataset.alt) this.mainImage.alt = thumb.dataset.alt;
      }

      selectPack(pack) {
        this.packs.forEach((p) => p.classList.remove('is-selected'));
        pack.classList.add('is-selected');
        this.syncPrices();
      }

      selectedInput() {
        const selected = this.packs.find((p) => {
          const input = p.querySelector('.zrc-ph__pack-input');
          return input && input.checked;
        });
        return selected ? selected.querySelector('.zrc-ph__pack-input') : null;
      }

      /* One rule for the top price row and every tile.
         One-time: now = price, was = compare-at (if any), badge = one-time saving.
         Refill:   now = plan price, was = compare-at, or the one-time price when
                   there is none and the plan is cheaper; badge = refill saving.
         A per-pack badge override replaces the "NN% OFF" text while a saving exists. */
      priceState(data, subscribing) {
        const sub = !!(subscribing && data.planId);
        const now = sub ? data.subPrice : data.price;
        let was = data.compare || '';
        if (sub && !was && data.subPrice !== data.price) was = data.price;
        const pct = parseInt(sub ? data.subSave || '0' : data.save || '0', 10);
        const badge = pct > 0 ? data.badge || pct + '% OFF' : '';
        return { now, was, badge };
      }

      paint(root, state, sel) {
        const now = root.querySelector(sel.now);
        const was = root.querySelector(sel.was);
        const badge = root.querySelector(sel.badge);
        if (now) {
          now.textContent = state.now;
          now.classList.toggle('zrc-ph__now--sale', !!state.was);
        }
        if (was) {
          was.hidden = !state.was;
          was.textContent = state.was;
        }
        if (badge) {
          badge.hidden = !state.badge;
          badge.textContent = state.badge;
        }
      }

      /* Push a selection into the form and announce it: the sticky bar
         listens for change events on these inputs. */
      setFormValue(input, value, disabled) {
        if (!input) return;
        const changed = input.value !== value || input.disabled !== disabled;
        input.value = value;
        input.disabled = disabled;
        if (changed) input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* Single-variant path: no pack picker, so the selling-plan data
         rides on the refill checkbox itself. */
      syncSolo() {
        if (!this.refill) return;
        const data = this.refill.dataset;
        const subscribing = !!(this.refill.checked && data.planId);
        this.setFormValue(this.planInput, subscribing ? data.planId : '', !subscribing);

        const heroPrice = this.querySelector('[data-hero-price]');
        if (heroPrice && data.price) this.paint(heroPrice, this.priceState(data, subscribing), HERO_SEL);
      }

      syncPrices() {
        const input = this.selectedInput();
        if (!input) {
          this.syncSolo();
          return;
        }

        const refillOn = !!(this.refill && this.refill.checked);
        const subscribing = !!(refillOn && input.dataset.planId);
        if (this.refill) {
          const box = this.refill.closest('.zrc-ph__refill');
          if (box) box.classList.toggle('is-on', refillOn);
        }

        this.setFormValue(this.variantInput, input.value, false);
        this.setFormValue(this.planInput, subscribing ? input.dataset.planId : '', !subscribing);

        // Keep the address in step so the chosen pack survives a reload or share.
        if (window.history && window.history.replaceState) {
          try {
            const url = new URL(window.location.href);
            url.searchParams.set('variant', input.value);
            window.history.replaceState({}, '', url);
          } catch (e) {
            /* noop */
          }
        }

        const heroPrice = this.querySelector('[data-hero-price]');
        if (heroPrice) this.paint(heroPrice, this.priceState(input.dataset, subscribing), HERO_SEL);

        // Every tile shows the price for the current purchase mode.
        this.packs.forEach((pack) => {
          const packInput = pack.querySelector('.zrc-ph__pack-input');
          if (!packInput) return;
          this.paint(pack, this.priceState(packInput.dataset, refillOn), PACK_SEL);
        });
      }
    }
  );
}
